import React, { useState, useEffect, useRef, useContext } from 'react';
import { FaPlay, FaPause, FaCaretDown, FaEllipsisH, FaStepForward, FaStepBackward, FaTimes } from 'react-icons/fa';
import { IoShareOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { AudioContext } from '../context/AudioContext';
import '../styles/AudioPlayer.css';

const AudioPlayer = ({ track, autoPlay }) => {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isExpanded, setIsExpanded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [menuVisible, setMenuVisible] = useState(false);
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const hasCountedPlay = useRef(false);
  const currentUser = getAuth().currentUser;
  const navigate = useNavigate();
  const { playNextTrack, playPreviousTrack, tracks } = useContext(AudioContext);

  useEffect(() => {
    if (track) {
      setIsPlaying(autoPlay);
      hasCountedPlay.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.load();
        setProgress(0);
        setCurrentTime(0);

        if (autoPlay) {
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.warn('Playback interrupted:', error);
            });
          }
        }
      }
    }
  }, [track, autoPlay]);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.warn('Playback interrupted:', error);
          });
        }
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    const updateProgress = () => {
      if (audioRef.current) {
        const current = audioRef.current.currentTime;
        const total = audioRef.current.duration;
        setCurrentTime(current);
        setDuration(total);
        if (total > 0) {
          setProgress((current / total) * 100);
        }

        if (!hasCountedPlay.current && current >= 30) {
          if (currentUser) {
            if (track.user?.firebaseUid === currentUser.uid) {
              console.log('Skipping play increment: this is the owner’s track.');
              hasCountedPlay.current = true;
              return;
            }

            axios
              .post('https://sync-api-troz.onrender.com/api/uploads/play', {
                trackId: track._id,
                listenedDuration: 30,
                firebaseUid: currentUser.uid,
              })
              .then(() => {
                console.log('Play count incremented after 30s of listening');
                hasCountedPlay.current = true;
              })
              .catch((error) => {
                console.error('Error incrementing play count:', error);
                hasCountedPlay.current = true;
              });
          } else {
            console.error('User not authenticated. Cannot increment play count.');
            hasCountedPlay.current = true;
          }
        }
      }
    };

    if (audioRef.current) {
      audioRef.current.addEventListener('timeupdate', updateProgress);
      audioRef.current.addEventListener('ended', playNextTrack);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('ended', playNextTrack);
      }
    };
  }, [track, currentUser, playNextTrack]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleSeek = (e) => {
    if (progressBarRef.current && audioRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      const offsetX = clientX - rect.left;
      const newProgress = Math.max(0, Math.min(1, offsetX / rect.width));
      const newTime = newProgress * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setProgress(newProgress * 100);
    }
  };

  const handleMouseDown = (e) => {
    handleSeek(e);
    document.addEventListener('mousemove', handleSeek);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleSeek);
    document.addEventListener('touchend', handleMouseUp);
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleSeek);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchmove', handleSeek);
    document.removeEventListener('touchend', handleMouseUp);
  };

  const handleShare = async () => {
    if (!track) {
      console.warn('No track available to share.');
      return;
    }

    const deepLink = `https://syncmusic.app/trackdetail/${track._id}`;
    const message = `🎵 Check out this track: "${track.title}" by ${track.user?.artistName || 'Unknown Artist'}\n\n🔥 Listen now: ${deepLink}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Share ${track.title}`,
          text: message,
          url: deepLink,
        });
      } else {
        await navigator.clipboard.writeText(message);
        window.alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing track:', error);
      window.alert('Failed to share track.');
    }
  };

  const handleLikeTrack = async () => {
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (track.user?.firebaseUid === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        { trackId: track._id, firebaseUid: currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Track liked successfully!');
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already liked this track'
          ? 'You have already liked this track.'
          : 'An error occurred while liking the track.'
      );
    }
  };

  const handleStarTrack = async () => {
    const firebaseUid = currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to star a track.');
      return;
    }

    if (track.user?.firebaseUid === firebaseUid) {
      window.alert("You can't star your own track.");
      return;
    }

    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/star', {
        trackId: track._id,
        userId: firebaseUid,
      });
      window.alert('Track starred successfully!');
    } catch (error) {
      console.error('Error starring track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already starred a track from this artist'
          ? 'You already starred a track from this user.'
          : 'An error occurred while starring the track.'
      );
    }
  };

  const handleGoToAlbum = async () => {
    try {
      const resp = await axios.get(
        `https://sync-api-troz.onrender.com/api/uploads/find-album-by-track?trackId=${track._id}`
      );
      if (resp.data && resp.data._id) {
        const albumId = resp.data._id;
        if (track.user?.firebaseUid === currentUser?.uid) {
          navigate(`/album/${albumId}`);
        } else {
          navigate(`/otheruserdiscography/${track.user?.firebaseUid}/album/${albumId}`);
        }
      } else {
        navigate(`/trackdetail/${track._id}`);
      }
    } catch (err) {
      console.log('Error finding album:', err);
      navigate(`/trackdetail/${track._id}`);
    }
  };

  const handleGoToArtist = () => {
    if (track.user?.firebaseUid) {
      navigate(`/otheruserdiscography/${track.user.firebaseUid}`);
      setIsExpanded(false);
    } else {
      console.warn('Firebase UID is missing; navigation aborted.');
    }
  };

  return (
    <div className={`audio-player ${isExpanded ? 'expanded' : 'minimized'}`}>
      <div className="audio-player-content">
        {!isExpanded ? (
          <div className="audio-player-minimized" onClick={() => setIsExpanded(true)}>
            <img src={track.coverPhoto} alt={track.title} className="cover-photo" />
            <div className="track-info">
              <p className="track-title-audio-player">{track.title}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoToArtist();
                }}
                className="track-artist"
              >
                {track.user?.artistName || 'Unknown Artist'}
              </button>
            </div>
            <button
              className="play-pause-btn"
              onClick={(e) => {
                e.stopPropagation();
                togglePlayPause();
              }}
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div className="audio-player-expanded">
            <div className="audio-player-widgets">
              <FaCaretDown
                className="minimize-caret"
                onClick={() => setIsExpanded(false)}
              />
              <FaEllipsisH
                className="menu-button"
                onClick={() => setMenuVisible(!menuVisible)}
              />
            </div>
            <img src={track.coverPhoto} alt={track.title} className="large-cover-photo" />
            <div className="expanded-track-info">
              <p className="track-title-audio-player">{track.title}</p>
              <button onClick={handleGoToArtist} className="track-artist">
                {track.user?.artistName || 'Unknown Artist'}
              </button>
            </div>
            <div
              className="expanded-progress-bar-container"
              onMouseDown={handleMouseDown}
              onTouchStart={handleMouseDown}
              ref={progressBarRef}
            >
              <div className="expanded-progress-bar" style={{ width: `${progress}%` }} />
              <div className="progress-thumb" style={{ left: `calc(${progress}% - 6px)` }} />
            </div>
            <div className="time-info">
              <span className="current-time">{formatTime(currentTime)}</span>
              <span className="duration-time">{formatTime(duration)}</span>
            </div>
            <div className="controls">
              <button className="control-button" onClick={playPreviousTrack}>
                <FaStepBackward size={30} />
              </button>
              <button className="expanded-play-pause" onClick={togglePlayPause}>
                {isPlaying ? <FaPause size={40} /> : <FaPlay size={40} />}
              </button>
              <button className="control-button" onClick={playNextTrack}>
                <FaStepForward size={30} />
              </button>
            </div>
            <div className="share-container">
              <button className="share-button" onClick={handleShare}>
                <IoShareOutline size={24} />
              </button>
            </div>
            {menuVisible && (
              <div className="menu-modal">
                <div className="menu-content">
                  <button className="menu-close-button" onClick={() => setMenuVisible(false)}>
                    <FaTimes size={20} />
                  </button>
                  <div className="menu-header">
                    <img src={track.coverPhoto} alt={track.title} className="menu-cover-photo" />
                    <div className="menu-track-info">
                      <p className="menu-track-title">{track.title}</p>
                      <p className="menu-artist">{track.user?.artistName || 'Unknown Artist'}</p>
                    </div>
                  </div>
                  <button className="menu-item" onClick={() => { 
                    handleLikeTrack(); 
                    setMenuVisible(false);
                    setIsExpanded(false) 
                    }}
                  >
                    Like Track
                  </button>
                  <button className="menu-item" onClick={() => { 
                    handleStarTrack(); 
                    setMenuVisible(false); 
                    setIsExpanded(false)
                    }}
                  >
                    Star Track
                  </button>
                  <button className="menu-item" onClick={() => { 
                    handleGoToAlbum(); 
                    setMenuVisible(false); 
                    setIsExpanded(false)
                    }}
                  >
                    Go to Album
                  </button>
                  <button className="menu-item" onClick={() => { 
                    handleGoToArtist(); 
                    setMenuVisible(false);
                    setIsExpanded(false) 
                    }}
                  >
                    Go to Artist
                  </button>
                  <button className="menu-item" onClick={() => { 
                    handleShare(); 
                    setMenuVisible(false);
                    setIsExpanded(false) 
                    }}
                  >
                    Share
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <audio ref={audioRef} src={track.filepath} />
    </div>
  );
};

export default AudioPlayer;